import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
    EnumWert,
    PublicMessstelle,
    PublicMessstellePruefpunkt
} from '../../../../Models/messstelle-qr.model';

import { MessstelleQrService } from '../../../../Services/messstelle-qr.service';
import { AuthService } from '../../../../api-authorization/authorize.service';


interface DatenEintrag {
    label: string;
    value: string;
}

interface PruefpunktGruppe {
    name: string;
    punkte: PublicMessstellePruefpunkt[];
}


@Component({
    selector: 'app-messstelle-public',
    templateUrl: './messstelle-public.component.html',
    styleUrls: ['./messstelle-public.component.css']
})
export class MessstellePublicComponent implements OnInit {

    token = '';

    messstelle: PublicMessstelle | null = null;

    isLoading = true;

    loadError = '';

    zusatzinfosOffen = false;


    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private service: MessstelleQrService,
        private authService: AuthService
    ) { }


    ngOnInit(): void {

        this.token =
            this.route.snapshot.paramMap.get('token') ?? '';

        if (!this.token) {

            this.loadError =
                'Der QR-Code ist ungültig.';

            this.isLoading = false;

            return;
        }

        this.lade();
    }


    private lade(): void {

        this.isLoading = true;

        this.loadError = '';

        this.service
            .getPublic(this.token)
            .subscribe({

                next: messstelle => {

                    this.messstelle = messstelle;

                    this.isLoading = false;
                },

                error: err => {

                    console.error(
                        'Öffentliche Messstelle konnte nicht geladen werden:',
                        err
                    );

                    if (
                        err?.status === 409
                        &&
                        err?.error?.code === 'qr_not_assigned'
                    ) {

                        this.loadError =
                            'Dieser QR-Code wurde noch keiner Messstelle zugeordnet.';

                    }
                    else if (err?.status === 404) {

                        this.loadError =
                            'Dieser QR-Code ist ungültig oder wurde deaktiviert.';

                    }
                    else {

                        this.loadError =
                            'Die Messstelle konnte nicht geladen werden.';

                    }

                    this.isLoading = false;
                }
            });
    }


    // ========================================================
    // LOGIN / REGULÄRER ZUGRIFF
    // ========================================================

    get istAngemeldet(): boolean {

        return this.authService.isAuthenticated();
    }


    anmelden(): void {

        /*
         * Der QR-Link selbst bleibt der Rücksprungpunkt.
         * Die Login-Komponente muss den Query-Parameter "returnUrl"
         * nach erfolgreicher Anmeldung verwenden.
         */
        this.router.navigate(
            ['/users/login'],
            {
                queryParams: {
                    returnUrl: this.router.url
                }
            }
        );
    }


    oeffneRegulaer(): void {

        if (
            !this.messstelle
            ||
            !this.messstelle.hasRegularAccess
        ) {
            return;
        }

        this.router.navigate([
            '/wartung/inbetriebnahme/messstelle',
            this.messstelle.id
        ]);
    }


    // ========================================================
    // MESSSTELLENDATEN
    // ========================================================

    /**
     * Alle in Values vorhandenen Excel-/Messstellendaten.
     *
     * Auch leere Werte bleiben erhalten und werden als "–" angezeigt,
     * damit "Zusatzinfos anzeigen" wirklich ALLE vorhandenen Felder zeigt.
     */
    get alleDaten(): DatenEintrag[] {

        if (!this.messstelle) {
            return [];
        }

        return Object
            .entries(this.messstelle.values ?? {})
            .map(
                ([label, value]) => ({
                    label,
                    value:
                        value != null
                            && String(value).trim().length > 0
                            ? String(value)
                            : '–'
                })
            );
    }


    /**
     * Kompakte Ansicht:
     * bevorzugt Kennzeichnung/KKS/... und Langtext.
     * Falls diese Spalten anders heißen, werden die ersten zwei Felder verwendet.
     */
    get hauptDaten(): DatenEintrag[] {

        const alle = this.alleDaten;

        if (alle.length <= 2) {
            return alle;
        }

        const gewaehlt: DatenEintrag[] = [];

        const finde = (
            begriffe: string[]
        ): DatenEintrag | undefined => {

            return alle.find(eintrag => {

                const label =
                    eintrag.label
                        .trim()
                        .toLocaleLowerCase('de');

                return begriffe.some(
                    begriff =>
                        label.includes(
                            begriff.toLocaleLowerCase('de')
                        )
                );
            });
        };


        const kennzeichnung =
            finde([
                'kennzeichnung',
                'kks',
                'bmk',
                'sks',
                'akz',
                'tag'
            ]);

        const langtext =
            finde([
                'langtext',
                'lang text',
                'beschreibung'
            ]);


        if (kennzeichnung) {
            gewaehlt.push(kennzeichnung);
        }

        if (
            langtext
            &&
            !gewaehlt.some(
                e => e.label === langtext.label
            )
        ) {
            gewaehlt.push(langtext);
        }


        for (const eintrag of alle) {

            if (gewaehlt.length >= 2) {
                break;
            }

            if (
                !gewaehlt.some(
                    e => e.label === eintrag.label
                )
            ) {
                gewaehlt.push(eintrag);
            }
        }

        return gewaehlt;
    }


    get zusatzDaten(): DatenEintrag[] {

        const hauptLabels =
            new Set(
                this.hauptDaten.map(
                    e => e.label
                )
            );

        return this.alleDaten.filter(
            e => !hauptLabels.has(e.label)
        );
    }


    toggleZusatzinfos(): void {

        this.zusatzinfosOffen =
            !this.zusatzinfosOffen;
    }


    // ========================================================
    // PRÜFPUNKTE
    // ========================================================

    get pruefpunktGruppen(): PruefpunktGruppe[] {

        if (!this.messstelle) {
            return [];
        }

        const map =
            new Map<string, PublicMessstellePruefpunkt[]>();

        for (const punkt of this.messstelle.pruefpunkte ?? []) {

            const name =
                (punkt.section ?? '').trim()
                || 'Allgemein';

            if (!map.has(name)) {
                map.set(name, []);
            }

            map.get(name)!.push(punkt);
        }

        return Array
            .from(map.entries())
            .map(
                ([name, punkte]) => ({
                    name,
                    punkte: [...punkte].sort(
                        (a, b) =>
                            a.sortOrder - b.sortOrder
                            ||
                            a.id - b.id
                    )
                })
            );
    }


    // ========================================================
    // ENUM-NORMALISIERUNG
    // ========================================================

    /**
     * Das Backend serialisiert Enums als Text (z. B. "Bestanden").
     * Ältere Antworten / lokale Daten können aber noch Zahlen enthalten.
     * Die QR-Seite versteht deshalb bewusst beide Formen.
     */
    private enumNummer(
        wert: EnumWert | null | undefined,
        texte: Record<string, number>
    ): number {

        if (typeof wert === 'number') {
            return wert;
        }

        if (wert == null) {
            return 0;
        }

        const text =
            String(wert).trim();

        const alsZahl =
            Number(text);

        if (
            text.length > 0
            &&
            Number.isFinite(alsZahl)
        ) {
            return alsZahl;
        }

        const normalisiert =
            text
                .replace(/[\s_-]/g, '')
                .toLocaleLowerCase('de');

        return texte[normalisiert] ?? 0;
    }


    pruefpunktWert(
        wert: EnumWert
    ): number {

        return this.enumNummer(
            wert,
            {
                nichtgeprueft: 0,
                bestanden: 1,
                nichtbestanden: 2,
                irrelevant: 3
            }
        );
    }


    pruefergebnisWert(
        wert: EnumWert
    ): number {

        return this.enumNummer(
            wert,
            {
                nichtgeprueft: 0,
                bestanden: 1,
                nichtbestanden: 2
            }
        );
    }


    signalStatusWert(
        wert: EnumWert
    ): number {

        return this.enumNummer(
            wert,
            {
                offen: 0,
                inbearbeitung: 1,
                getestet: 2,
                freigegeben: 3,
                fehlernacharbeit: 4
            }
        );
    }

    private findeDatenfeld(...begriffe: string[]): string {

        if (!this.messstelle?.values) {
            return '–';
        }

        const eintrag = Object.entries(this.messstelle.values).find(([label]) => {
            const normalized = label.trim().toLowerCase();
            return begriffe.some(begriff => normalized.includes(begriff.toLowerCase()));
        });

        const value = eintrag?.[1];

        return value != null && String(value).trim().length > 0
            ? String(value)
            : '–';
    }

    get kennzeichnungLabel(): string {

        if (!this.messstelle?.values) {
            return 'Kennzeichnung';
        }

        const labels = Object.keys(this.messstelle.values);

        const match = labels.find(label => {
            const normalized = label.trim().toLowerCase();

            return (
                normalized.includes('kks') ||
                normalized.includes('bmk') ||
                normalized.includes('sks') ||
                normalized.includes('akz') ||
                normalized.includes('kennzeichnung') ||
                normalized.includes('tag')
            );
        });

        return match || 'Kennzeichnung';
    }

    get kennzeichnungWert(): string {
        return this.findeDatenfeld(
            'kks',
            'bmk',
            'sks',
            'akz',
            'kennzeichnung',
            'tag'
        );
    }

    get langtextWert(): string {
        return this.findeDatenfeld(
            'langtext',
            'lang text',
            'beschreibung'
        );
    }

    get projektAnzeige(): string {

        const nummer = this.messstelle?.projektNummer?.trim() || '';
        const name = this.messstelle?.projektName?.trim() || '';

        if (nummer && name) {
            return `${nummer} · ${name}`;
        }

        return nummer || name || '–';
    }


    // ========================================================
    // FORTSCHRITT / BESCHRIFTUNGEN
    // ========================================================

    get pruefpunkteGesamt(): number {

        return this.messstelle?.pruefpunkte?.length ?? 0;
    }


    get pruefpunkteErledigt(): number {

        return (
            this.messstelle?.pruefpunkte
                ?.filter(
                    p =>
                        this.pruefpunktWert(p.ergebnis) !== 0
                )
                .length
            ??
            0
        );
    }


    get fortschrittProzent(): number {

        if (this.pruefpunkteGesamt === 0) {
            return 0;
        }

        return Math.round(
            this.pruefpunkteErledigt
            /
            this.pruefpunkteGesamt
            *
            100
        );
    }


    pruefpunktText(
        ergebnis: EnumWert
    ): string {

        switch (
        this.pruefpunktWert(ergebnis)
        ) {

            case 1:
                return 'Bestanden';

            case 2:
                return 'Nicht bestanden';

            case 3:
                return 'Irrelevant';

            default:
                return 'Nicht geprüft';
        }
    }


    pruefergebnisText(
        ergebnis: EnumWert
    ): string {

        switch (
        this.pruefergebnisWert(ergebnis)
        ) {

            case 1:
                return 'Bestanden';

            case 2:
                return 'Nicht bestanden';

            default:
                return 'Noch nicht geprüft';
        }
    }


    signalStatusText(
        status: EnumWert
    ): string {

        switch (
        this.signalStatusWert(status)
        ) {

            case 1:
                return 'In Bearbeitung';

            case 2:
                return 'Getestet';

            case 3:
                return 'Freigegeben';

            case 4:
                return 'Fehler / Nacharbeit';

            default:
                return 'Offen';
        }
    }


    // ========================================================
    // BILDER
    // ========================================================

    bildUrl(
        bildId: number
    ): string {

        return this.service.publicBildUrl(
            this.token,
            bildId
        );
    }


    neuLaden(): void {

        this.lade();
    }
}
